import { SECTION_NAMES } from '@bridebook/web/lib/wedding-website/constants';
import { useTranslation } from 'next-i18next';
import { MouseEventHandler, useCallback } from 'react';
import { MOTIF_SECTION_IDS } from '../sections/motif-section-hooks';

/**
 * Anchor menu of the couple's visible content sections — the one piece of "real website" chrome CSS can't synthesize
 * from the section DOM. Hidden by default (base CSS `.bb-ww__nav{display:none}`); a theme opts in and styles it into a
 * sticky top bar, a side index, a dot rail… Labels reuse the section-title translation keys.
 *
 * Clicks scroll scoped to the nearest `.bb-ww` instance rather than relying on document-wide anchor navigation: the
 * editor panel and the full-page preview modal can be mounted at the same time, so ids are not unique per document.
 */
export const MotifNav = ({ sectionKeys }: { sectionKeys: string[] }) => {
  const { t } = useTranslation('weddingWebsite');

  const labels: Record<string, string> = {
    [SECTION_NAMES.OUR_STORY]: t('previewMode.tile.ourStory.title'),
    [SECTION_NAMES.SCHEDULE]: t('previewMode.tile.schedule.title'),
    [SECTION_NAMES.PRIMARY_VENUE]: t('previewMode.tile.ourVenue.title'),
    [SECTION_NAMES.GIFT_LIST]: t('previewMode.tile.giftList.title'),
    [SECTION_NAMES.HOTELS]: t('previewMode.tile.hotels.title'),
    [SECTION_NAMES.FAQ]: t('previewMode.tile.faq.title2'),
  };

  const handleClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    if (!href) return;
    const target = e.currentTarget.closest('.bb-ww')?.querySelector(href);
    if (!target) return;
    const initialTop = target.getBoundingClientRect().top;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // Smooth scrollIntoView silently no-ops inside the editor's scaled preview scroller (Chromium + transformed
    // content). If the target hasn't moved shortly after, fall back to an instant jump — the themes' per-section
    // scroll-margin-top (which keeps the title clear of a sticky nav) applies to both paths.
    window.setTimeout(() => {
      if (Math.abs(target.getBoundingClientRect().top - initialTop) < 2) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }, 250);
  }, []);

  const items = sectionKeys.filter((key) => MOTIF_SECTION_IDS[key] && labels[key]);
  if (items.length === 0) return null;

  return (
    <nav className="bb-ww__nav" aria-label={t('motif.nav.label')}>
      {items.map((key) => (
        <a
          key={key}
          className="bb-ww__nav-link"
          href={`#${MOTIF_SECTION_IDS[key]}`}
          onClick={handleClick}>
          {labels[key]}
        </a>
      ))}
    </nav>
  );
};
